import React, { useEffect, useState } from 'react';

import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import ControlGroup from '@amzn/meridian/control-group';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import _ from 'lodash';
import { type RegionTaskModification } from 'src/services/tasks/types';
import {
  setFormTasksTableData,
  setSelectedFormTasksTableRow,
} from 'src/store/adminConfigPageSlice/regionTasksPageSlice/regionTasksPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';

// **** Components **** //

/**
 * Default component
 */
const AssignModificationsModal = () => {
  const dispatch = useAppDispatch();
  const { selectedTaskFormTableRow, taskFormTableData } = useAppSelector((state) => state.regionTasksConfigPage);
  const allModifications = useAppSelector((state) => state.modifications.modifications);
  const [updatableList, setUpdatableList] = useState<RegionTaskModification[]>([]);
  const close = () => dispatch(setSelectedFormTasksTableRow(null));

  // Initialize the 'updatableList' when opening
  useEffect(() => {
    if (selectedTaskFormTableRow?.type === 'task') {
      setUpdatableList(selectedTaskFormTableRow.modifications);
    } else if (selectedTaskFormTableRow?.type === 'task-group') {
      setUpdatableList(selectedTaskFormTableRow.commonModifications);
    }
  }, [selectedTaskFormTableRow]);

  // Remove one modification
  const dismissTag = (modificationId: number) => {
    const newMods = updatableList.filter((m) => m.modificationId != modificationId);
    setUpdatableList(newMods);
  };

  // Submit changes, note this will update redux but not persist the changes
  const apply = () => {
    if (!selectedTaskFormTableRow) return;
    const rowClone = _.cloneDeep(selectedTaskFormTableRow);
    if (rowClone.type === 'task') {
      rowClone.modifications = updatableList;
    } else if (selectedTaskFormTableRow?.type === 'task-group') {
      rowClone.commonModifications = updatableList;
    }
    const rowIdx = taskFormTableData.findIndex(({ entityId }) => entityId === rowClone.entityId);
    const tableRowsCopy = [...taskFormTableData];
    tableRowsCopy[rowIdx] = rowClone;
    dispatch(setFormTasksTableData(tableRowsCopy));
    close();
  };

  return (
    <Modal
      open={!!selectedTaskFormTableRow?.entityId}
      title={`${selectedTaskFormTableRow?.label} Modifications`}
      onClose={close}
    >
      {/* Header */}
      <Row alignmentHorizontal="justify">
        <Text>{updatableList.length} active</Text>
        <Button type="link" onClick={() => setUpdatableList([])}>
          Clear All
        </Button>
      </Row>

      {/* Table  */}
      <Row spacingInset="none none 400 none" wrap="down" maxWidth="550px" spacing="300">
        {updatableList.map(({ name, modificationId }) => (
          <Tag
            key={modificationId}
            data-testid="modification-tag"
            type="neutral"
            onClose={() => dismissTag(modificationId)}
          >
            {name}
          </Tag>
        ))}
      </Row>
      <Column type="outline" overflowY="scroll" minWidth="550px" maxHeight="400px" spacingInset="400">
        <ControlGroup
          value={updatableList.map((m) => m.modificationId)}
          onChange={(ids) => {
            const newMods = allModifications
              .filter((m) => ids.includes(m.id))
              .map(({ id, ...other}) => ({ modificationId: id, ...other }))
            setUpdatableList(newMods);
          }}
        >
          {allModifications.map((modification) => (
            <Checkbox key={modification.id} value={modification.id}>
              {modification.name}
            </Checkbox>
          ))}
        </ControlGroup>
      </Column>

      {/* Footer  */}
      <ModalFooter>
        <Row alignmentHorizontal="end">
          <Button type="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="primary" onClick={apply}>
            Apply
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

// **** Export **** //

export default AssignModificationsModal;
